export const genId = () => `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const HTML_ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, ch => HTML_ENTITY_MAP[ch] || ch);
}

/**
 * Strip every tag except a tiny allow-list (span / br / b / i / u / strong / em).
 * Allowed spans may carry inline font-size only — everything else is dropped.
 * Used when committing in-canvas rich-text edits so a paste of arbitrary HTML
 * can't smuggle scripts or styles into the saved block content.
 *
 * NOTE: This is intentionally minimal. We don't run a full HTML sanitiser
 * (DOMPurify et al) since the editor only emits the spans it generates itself
 * — but we still scrub paste input through the same function before saving.
 */
export function sanitizeInlineHtml(html: string): string {
  if (!html) return '';
  // Parse via a DOM fragment so we can walk nodes structurally.
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const allowedTags = new Set(['SPAN', 'BR', 'B', 'I', 'U', 'STRONG', 'EM', 'P', 'DIV']);
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    const childHtml = Array.from(el.childNodes).map(walk).join('');
    if (!allowedTags.has(el.tagName)) return childHtml; // unwrap unknown elements
    // BR has no children, render as a void tag.
    if (el.tagName === 'BR') return '<br>';
    // DIV / P become block-level line breaks; flatten to plain text so the
    // contentEditable round-trip doesn't accumulate empty wrappers.
    if (el.tagName === 'DIV' || el.tagName === 'P') {
      // Insert a newline before the content so consecutive blocks stay
      // visually separated when re-rendered.
      return childHtml + '<br>';
    }
    if (el.tagName === 'SPAN') {
      const fs = el.style.fontSize;
      const fw = el.style.fontWeight;
      const ts = el.style.textDecoration;
      const fst = el.style.fontStyle;
      const parts: string[] = [];
      if (fs && /^\d+(\.\d+)?(px|em|rem|%)$/.test(fs)) parts.push(`font-size:${fs}`);
      if (fw && /^(\d{3}|bold|normal)$/.test(fw)) parts.push(`font-weight:${fw}`);
      if (ts && /(underline|line-through)/.test(ts)) parts.push(`text-decoration:${ts}`);
      if (fst && /(italic|oblique)/.test(fst)) parts.push(`font-style:${fst}`);
      return parts.length ? `<span style="${parts.join(';')}">${childHtml}</span>` : childHtml;
    }
    return `<${el.tagName.toLowerCase()}>${childHtml}</${el.tagName.toLowerCase()}>`;
  };
  const out = Array.from(tmpl.content.childNodes).map(walk).join('');
  // Strip a trailing `<br>` that the DIV/P flattening can leave behind so
  // saved content doesn't grow an extra blank line on every commit.
  return out.replace(/<br>\s*$/i, '');
}

/**
 * Decide whether a block's text content should be rendered as HTML
 * (via dangerouslySetInnerHTML) or as plain text. Blocks authored before
 * the rich-text editor existed have plain-text content and no isRichText
 * flag — those keep rendering as plain text so existing campaigns are
 * unchanged. Newly rich-edited blocks set `props.isRichText = true`.
 */
export function isRichTextBlock(block: { props?: Record<string, any> | undefined }): boolean {
  return !!block.props?.isRichText;
}
